import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { getEvaluationStatusColorClass, getEvalTypePillColorClass } from '../Utils/ColorUtils';
import EvaluationStatus from '../EvaluationComponents/Utils/EvaluationStatus';
import CreateEvaluation from '../EvaluationComponents/CreateEvaluation';
import CreateEvaluationWizard from '../CreateEvaluationComponents/CreateEvaluationWizard';
import Navigation from './Components/Navigation';

/**
 * Evaluations Page
 * Displays a page where users can create any type of evaluation without context
 */
const Evaluations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useSession();
  const [showCreateEvalPopup, setShowCreateEvalPopup] = useState(false);
  const [showCreateEvalWizard, setShowCreateEvalWizard] = useState(false);
  const [wizardEvaluationId, setWizardEvaluationId] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pageSize] = useState(20); // Number of items per page
  const [currentOffset, setCurrentOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  
  // Filter state
  const [baseCriteria, setBaseCriteria] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Set document title
  useEffect(() => {
    document.title = "Evaluations";
  }, []);
  
  // Function to fetch user evaluations with pagination and filters
  const fetchUserEvaluations = async (offset = 0) => {
    try {
      setLoading(true);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Create URL with user ID parameter, pagination, and filters
      const url = new URL(API_ENDPOINTS.GET_USER_EVALUATIONS);
      url.searchParams.append('uid', user.id);
      url.searchParams.append('limit', pageSize);
      url.searchParams.append('offset', offset);
      
      // Add base criteria filter if not 'all'
      if (baseCriteria !== 'all') {
        url.searchParams.append('type', baseCriteria);
      }
      
      // Add search query if not empty
      if (searchQuery.trim() !== '') {
        url.searchParams.append('search', searchQuery.trim());
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const error = new Error(`Server responded with status: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data with error handling for empty responses
      let data = [];
      try {
        data = await response.json();
        console.log('User Evaluations:', data);
      } catch (parseError) {
        console.log('Empty or invalid response received');
        data = [];
      }
      
      // Check if this is the last page (if we received fewer items than the page size)
      setIsLastPage(data.length < pageSize);
      
      setEvaluations(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching user evaluations:', err);
      console.log('Error status code:', err.status);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setError(err.message || 'Failed to fetch user evaluations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user evaluations when component mounts, offset changes, or base criteria changes
  useEffect(() => {
    fetchUserEvaluations(currentOffset);
  }, [currentOffset, baseCriteria]);
  
  // Handle next page
  const handleNextPage = () => {
    if (!isLastPage) {
      const newOffset = currentOffset + pageSize;
      setCurrentOffset(newOffset);
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Handle previous page
  const handlePrevPage = () => {
    if (currentOffset > 0) {
      const newOffset = Math.max(0, currentOffset - pageSize);
      setCurrentOffset(newOffset);
      setCurrentPage(currentPage - 1);
    }
  };

  // Function to handle create evaluation submission 
  const handleCreateEvaluation = async (data) => {
    console.log('Evaluation created:', data);
    
    // Show success message
    alert(`Evaluation "${data.evaluationName}" is being generated. It should be ready in approximately 10 minutes - please come back then.`);
    
    // Refresh the evaluations list - reset to first page when new evaluation is added
    setCurrentOffset(0);
    setCurrentPage(1);
    fetchUserEvaluations(0);
  };

  // Function to handle opening the evaluation details page
  const handleOpenEvaluationDetails = (evaluationId) => {
    navigate(`/evaluation-details?eid=${evaluationId}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[76px]">
      <Navigation title="Evaluations" showBackButton={false} />
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="border-b border-gray-200 pb-3 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-gray-800">
                All Evaluation Templates
              </h2>
              
              {/* Filters */}
              <div className="flex items-center space-x-4 ml-6">
                {/* Base Criteria Filter */}
                <div className="flex items-center">
                  <select 
                    id="baseCriteria" 
                    value={baseCriteria}
                    onChange={(e) => {
                      setBaseCriteria(e.target.value);
                      setCurrentOffset(0); // Reset to first page when filter changes
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Evaluation Criteria</option>
                    <option value="VSJob">Job (VSJob)</option>
                    <option value="VSResume">Resume (VSResume)</option>
                    <option value="VSSkillset">Custom (VSSkillset)</option>
                  </select>
                </div>
                
                {/* Search Filter */}
                <div className="flex items-center">
                  <div className="relative flex">
                    <input 
                      type="text" 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by job or talent ID" 
                      className="border border-gray-300 rounded-l-md pl-8 pr-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 bg-gray-100"
                      disabled
                    />
                    <svg 
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <button
                      onClick={() => {
                        setCurrentOffset(0); // Reset to first page when search is performed
                        setCurrentPage(1);
                        fetchUserEvaluations(0);
                      }}
                      className="bg-blue-300 text-white px-3 py-1 rounded-r-md text-sm focus:outline-none cursor-not-allowed"
                      disabled
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Wizard Button - Only visible for Superadmin */}
              {/* {user?.role === 'Superadmin' && ( */}
                <button 
                  onClick={() => setShowCreateEvalWizard(true)}
                  className="px-3 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
                  title="Create Evaluation Wizard"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Create New Evaluation
                </button>
              {/* )} */}
              
              {/* Regular Create Evaluation Button */}
              {/* <button 
                onClick={() => setShowCreateEvalPopup(true)}
                className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Create New Evaluation OLD
              </button> */}
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">Loading evaluations...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <p className="text-gray-600 mt-2">Please try again or refresh the page.</p>
          </div>
        ) : evaluations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No evaluations available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Talent</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {evaluations.map((evaluation) => {
                    // Format the date
                    const createdDate = new Date(evaluation.created_at);
                    const formattedDate = createdDate.toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    return (
                      <tr 
                        key={evaluation.evaluation_id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleOpenEvaluationDetails(evaluation.evaluation_id)}
                      >
                        <td className="px-6 py-4 text-base font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-md">{evaluation.name}</p>
                                {evaluation.is_default && (
                                  <svg 
                                    className="w-5 h-5 text-green-500" 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    title="Default Evaluation"
                                  >
                                    <path d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"></path>
                                  </svg>
                                )}
                              </div>
                              <p className="text-s text-gray-500">{formattedDate}</p>
                              {/* <p className="text-xs text-gray-500">{evaluation.evaluation_id}</p> */}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-base text-gray-900">
                          <div>
                            {evaluation.evaluation_role?.subCategory ? (
                              <>
                                <p className="font-medium">{evaluation.evaluation_role.subCategory}</p>
                                {evaluation.type && (() => {
                                  const pillInfo = getEvalTypePillColorClass(evaluation.type);
                                  return (
                                    <span className={`px-0 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${pillInfo.colorClasses}`}>
                                      {pillInfo.text}
                                    </span>
                                  );
                                })()}
                              </>
                            ) : (
                              evaluation.type && (() => {
                                const pillInfo = getEvalTypePillColorClass(evaluation.type);
                                return (
                                  <span className={`px-0 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${pillInfo.colorClasses}`}>
                                    {pillInfo.text}
                                  </span>
                                );
                              })()
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            {evaluation.jobs?.company_name ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click event
                                    navigate(`/job-details?jid=${evaluation.job_id}`);
                                  }}
                                  className="font-medium text-base mb-2 text-blue-600 hover:text-blue-800 transition-colors cursor-pointer text-left"
                                  title={`Go to Job Details: ${evaluation.job_id}`}
                                >
                                  {evaluation.jobs.company_name}
                                </button>
                                {evaluation.job_id && (
                                  <p className="text-xs text-gray-500">{evaluation.job_id.substring(0, 8)}...</p>
                                )}
                              </>
                            ) : evaluation.job_id ? (
                              <p className="text-xs text-gray-500">{evaluation.job_id.substring(0, 8)}...</p>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            {evaluation.talent?.fname && evaluation.talent?.lname ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click event
                                    navigate(`/talentdetails?tid=${evaluation.talent_id}`);
                                  }}
                                  className="font-medium text-base mb-2 text-blue-600 hover:text-blue-800 transition-colors cursor-pointer text-left"
                                  title={`Go to Talent Details: ${evaluation.talent_id}`}
                                >
                                  {evaluation.talent.fname} {evaluation.talent.lname}
                                </button>
                                {evaluation.talent_id && (
                                  <p className="text-xs text-gray-500">{evaluation.talent_id.substring(0, 8)}...</p>
                                )}
                              </>
                            ) : evaluation.talent_id ? (
                              <p className="text-xs text-gray-500">{evaluation.talent_id.substring(0, 8)}...</p>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-base text-gray-500">
                          <EvaluationStatus status={evaluation.status} showDot={true} size="sm" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {/* Wizard Button - Only show if status is not evs_available */}
                            {evaluation.status !== 'evs_available' && (
                              <button 
                                className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors"
                                title="Edit in Wizard"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click event
                                  setWizardEvaluationId(evaluation.evaluation_id);
                                  setShowCreateEvalWizard(true);
                                }}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                              </button>
                            )}
                            
                            {/* Delete Button */}
                            <button 
                              className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click event
                                
                                // Show confirmation dialog
                                if (window.confirm(`Are you sure you want to delete evaluation "${evaluation.name}"?`)) {
                                // User confirmed, send delete request
                                const deleteEvaluation = async () => {
                                  try {
                                    // Create URL with evaluation ID parameter
                                    const url = new URL(API_ENDPOINTS.DELETE_EVALUATION);
                                    url.searchParams.append('eid', evaluation.evaluation_id);
                                    
                                    const response = await fetch(url, {
                                      method: 'GET',
                                      headers: {
                                        'Authorization': `Bearer ${user.access_token}`
                                      }
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error(`Error: ${response.status} ${response.statusText}`);
                                    }
                                    
                                    // Remove the deleted evaluation from the list
                                    setEvaluations(prevEvaluations => 
                                      prevEvaluations.filter(item => item.evaluation_id !== evaluation.evaluation_id)
                                    );

                                    // // Refresh the evaluations list 
                                    // fetchUserEvaluations(currentOffset);
                                  } catch (err) {
                                    console.error('Error deleting evaluation:', err);
                                    alert('Failed to delete evaluation. Please try again.');
                                  }
                                };
                                
                                deleteEvaluation();
                              }
                            }}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing page {currentPage} {isLastPage ? '(last page)' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentOffset === 0}
                  className={`px-4 py-2 border rounded-md ${
                    currentOffset === 0 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={isLastPage}
                  className={`px-4 py-2 border rounded-md ${
                    isLastPage 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Create Evaluation Popup */}
      {showCreateEvalPopup && (
        <CreateEvaluation 
          isOpen={showCreateEvalPopup}
          onClose={() => setShowCreateEvalPopup(false)}
          onSubmit={handleCreateEvaluation}
          job={null}
          talent={null}
          contextType={null} // No context
        />
      )}

      {/* Create Evaluation Wizard Popup */}
      {showCreateEvalWizard && (
        <CreateEvaluationWizard 
          isOpen={showCreateEvalWizard}
          onClose={() => {
            setShowCreateEvalWizard(false);
            setWizardEvaluationId(null); // Reset the evaluation ID when closing
          }}
          evaluation_id={wizardEvaluationId}
        />
      )}
    </div>
  );
};

export default Evaluations;
